import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { registerPlugin } from '@capacitor/core';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

const isNative = () => {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};

export const LocationService = {
  async requestPermissions() {
    if (!isNative()) return true;
    try {
      const perm = await Geolocation.requestPermissions();
      const status = perm?.location || perm?.coarseLocation || 'granted';
      return status === 'granted' || status === 'limited';
    } catch {
      return false;
    }
  },

  async getCurrentPosition(options = { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }) {
    if (isNative()) {
      const permOk = await this.requestPermissions();
      if (!permOk) throw new Error('Location permission not granted');
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: options.timeout || 10000 });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude, heading: pos.coords.heading };
    }

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude, heading: position.coords.heading }),
        (error) => reject(error),
        options
      );
    });
  },

  async watchPosition(onUpdate, onError, options = { enableHighAccuracy: true }) {
    if (isNative()) {
      const permOk = await this.requestPermissions();
      if (!permOk) throw new Error('Location permission not granted');
      const id = await Geolocation.watchPosition({ enableHighAccuracy: true }, (pos, err) => {
        if (err) { onError && onError(err); return; }
        if (!pos) return;
        // Pass a Position-like object for compatibility with web code
        onUpdate && onUpdate({
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            altitude: pos.coords.altitude,
            speed: pos.coords.speed,
            heading: pos.coords.heading
          },
          timestamp: pos.timestamp || Date.now()
        });
      });
      return { clear: async () => { await Geolocation.clearWatch({ id }); } };
    }

    if (!navigator.geolocation) throw new Error('Geolocation not supported');
    const id = navigator.geolocation.watchPosition(
      (position) => onUpdate && onUpdate(position),
      (error) => onError && onError(error),
      options
    );
    return { clear: () => { try { navigator.geolocation.clearWatch(id); } catch {} } };
  }
};

// Background tracking scaffolding
export const BackgroundLocation = {
  async start(options = { distanceFilter: 25 }) {
    if (!isNative()) throw new Error('Background tracking only on native');
    try {
      const watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Tracking your trail... stop to save battery.',
          backgroundTitle: 'Okio Tracking',
          requestPermissions: true,
          stale: false,
          distanceFilter: options.distanceFilter ?? 25,
        },
        (location, error) => {
          try {
            if (error) {
              if (error.code === 'NOT_AUTHORIZED') {
                BackgroundGeolocation.openSettings();
              }
              return;
            }
            window.dispatchEvent(new CustomEvent('okio:bg_location', { detail: location }));
          } catch {}
        }
      );
      return watcherId;
    } catch (e) {
      throw e;
    }
  },
  async stop(watcherId) {
    try { await BackgroundGeolocation.removeWatcher({ id: watcherId }); } catch {}
  }
};


